import { cx } from "../../lib/cx.js";
import { Button } from "../Button/Button.js";
import { IconButton } from "../IconButton/IconButton.js";
/* empty css             */
import { useId, useRef, useState } from "react";
import { Fragment as Fragment$1, jsx, jsxs } from "react/jsx-runtime";
//#region src/components/Dropzone/Dropzone.tsx
/** Whether `file` matches an `accept` list: extensions (".csv"), MIME types or "type/*". */
function acceptsFile(file, accept) {
	if (!accept) return true;
	const name = file.name.toLowerCase();
	const type = file.type.toLowerCase();
	return accept.split(",").map((rule) => rule.trim().toLowerCase()).filter(Boolean).some((rule) => {
		if (rule.startsWith(".")) return name.endsWith(rule);
		if (rule.endsWith("/*")) return type.startsWith(rule.slice(0, -1));
		return type === rule;
	});
}
var listNames = (files) => files.map((file) => file.name).join(", ");
/**
* A place to drop files, with a Browse button as the keyboard way in (a click anywhere
* on it browses too). Files are checked against `accept`; the accepted ones go to
* `onFiles`, and their names are announced.
*/
function Dropzone({ accept, multiple = false, onFiles, onReject, title = multiple ? "Drop files here" : "Drop a file here", description = multiple ? "or browse · multiple files supported" : "or browse", browseLabel = multiple ? "Browse files" : "Browse", rejectMessage = (files) => `${listNames(files)} ${files.length === 1 ? "isn't" : "aren't"} accepted. Use ${accept}.`, disabled = false, invalid = false, name, className, ...rest }) {
	const id = useId();
	const inputRef = useRef(null);
	const depth = useRef(0);
	const [dragOver, setDragOver] = useState(false);
	const [rejection, setRejection] = useState(null);
	const [announcement, setAnnouncement] = useState("");
	const take = (list) => {
		const files = Array.from(list ?? []);
		if (files.length === 0) return;
		const chosen = multiple ? files : files.slice(0, 1);
		const accepted = chosen.filter((file) => acceptsFile(file, accept));
		const rejected = chosen.filter((file) => !acceptsFile(file, accept));
		const messages = [];
		if (accepted.length) {
			onFiles(accepted);
			messages.push(`Added ${listNames(accepted)}.`);
		}
		if (rejected.length) {
			onReject?.(rejected);
			const message = rejectMessage(rejected);
			setRejection(message);
			messages.push(message);
		} else setRejection(null);
		setAnnouncement(messages.join(" "));
	};
	const browse = () => {
		if (!disabled) inputRef.current?.click();
	};
	const onDragEnter = (event) => {
		if (disabled) return;
		event.preventDefault();
		depth.current += 1;
		setDragOver(true);
	};
	const onDragOver = (event) => {
		if (disabled) return;
		event.preventDefault();
		event.dataTransfer.dropEffect = "copy";
	};
	const onDragLeave = () => {
		depth.current = Math.max(0, depth.current - 1);
		if (depth.current === 0) setDragOver(false);
	};
	const onDrop = (event) => {
		if (disabled) return;
		event.preventDefault();
		depth.current = 0;
		setDragOver(false);
		take(event.dataTransfer.files);
	};
	const onClick = (event) => {
		if (event.target.closest("button, input")) return;
		browse();
	};
	const onChange = (event) => {
		take(event.target.files);
		event.target.value = "";
	};
	return /* @__PURE__ */ jsxs("div", {
		role: "group",
		"aria-labelledby": `${id}title`,
		"aria-describedby": rejection ? `${id}description ${id}error` : `${id}description`,
		"aria-disabled": disabled || void 0,
		...rest,
		className: cx("ds-dropzone", dragOver && "ds-dropzone--drag-over", (invalid || rejection) && "ds-dropzone--invalid", disabled && "ds-dropzone--disabled", className),
		onClick,
		onDragEnter,
		onDragOver,
		onDragLeave,
		onDrop,
		children: [
			/* @__PURE__ */ jsx("span", {
				id: `${id}title`,
				className: "ds-dropzone__title",
				children: title
			}),
			/* @__PURE__ */ jsx("span", {
				id: `${id}description`,
				className: "ds-dropzone__description",
				children: description
			}),
			/* @__PURE__ */ jsx(Button, {
				variant: "secondary",
				size: "sm",
				leadingIcon: "upload",
				className: "ds-dropzone__browse",
				disabled,
				onClick: browse,
				children: browseLabel
			}),
			rejection && /* @__PURE__ */ jsx("span", {
				id: `${id}error`,
				className: "ds-dropzone__error",
				children: rejection
			}),
			/* @__PURE__ */ jsx("input", {
				ref: inputRef,
				type: "file",
				hidden: true,
				tabIndex: -1,
				accept,
				multiple,
				name,
				disabled,
				onChange
			}),
			/* @__PURE__ */ jsx("span", {
				className: "ds-dropzone__sr",
				role: "status",
				children: announcement
			})
		]
	});
}
/** A file size: "820 B", "12.4 KB", "3.1 MB". */
function formatFileSize(bytes) {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / 1048576).toFixed(1)} MB`;
}
/**
* The files chosen so far, each with its size and a remove button. Removing one moves
* focus to the next file's button (or the previous, or the list) and announces it.
*/
function FileList({ files, onRemove, removeLabel = (file) => `Remove ${file.name}`, removedMessage = (file) => `Removed ${file.name}`, "aria-label": ariaLabel = "Selected files", className, ...rest }) {
	const listRef = useRef(null);
	const [announcement, setAnnouncement] = useState("");
	const remove = (file, index) => {
		onRemove?.(file, index);
		setAnnouncement(removedMessage(file));
		requestAnimationFrame(() => {
			const buttons = listRef.current?.querySelectorAll(".ds-file-list__remove");
			(buttons?.[Math.min(index, buttons.length - 1)] ?? listRef.current)?.focus();
		});
	};
	return /* @__PURE__ */ jsxs(Fragment$1, { children: [/* @__PURE__ */ jsx("ul", {
		ref: listRef,
		tabIndex: -1,
		"aria-label": ariaLabel,
		...rest,
		className: cx("ds-file-list", className),
		children: files.map((file, index) => /* @__PURE__ */ jsxs("li", {
			className: "ds-file-list__row",
			children: [
				/* @__PURE__ */ jsx("span", {
					className: "ds-file-list__name",
					children: file.name
				}),
				file.size !== void 0 && /* @__PURE__ */ jsx("span", {
					className: "ds-file-list__size",
					children: formatFileSize(file.size)
				}),
				onRemove && /* @__PURE__ */ jsx(IconButton, {
					icon: "x",
					size: "sm",
					className: "ds-file-list__remove",
					"aria-label": removeLabel(file),
					onClick: () => remove(file, index)
				})
			]
		}, file.id ?? file.name))
	}), /* @__PURE__ */ jsx("span", {
		className: "ds-file-list__sr",
		role: "status",
		children: announcement
	})] });
}
//#endregion
export { Dropzone, FileList, acceptsFile, formatFileSize };

//# sourceMappingURL=Dropzone.js.map